// src/oura/merchant/docAsks.ts
//
// The documents shared with the merchant, and the one queue of everything
// that needs them. A doc with questions waiting on them needs them as much as
// an open question, so the Decisions sentence, Home, Work, the nav badge and
// "Start answering" all read one list here (`needsYouQueue`) and never
// disagree about a count.
//
// Pure. The docs are `ClientPagePayload.documents`: absent means Oura does not
// read docs for this business yet (count nothing, show nothing); [] means
// nothing is shared with them. Every state and date is the server's; nothing
// here decides one.
//
// Merchant words only: no codes, no staff states. Lateness is a date ("Was due
// Thu 24 Sep"), never a day count. A doc's codes turn into the plain names of
// the work they cover ("What it's for").

import { codeChipsOf, type MerchantDocState, type MerchantDocument } from "@service/aeo/clientDocs";
import { DOC_EXPECTED, merchantDocName } from "@service/aeo/clientDocsRules";
import type { ClientPagePayload } from "@service/aeo/clientReview";
import { FILE_TYPE, docDate, merchantReplyByText, outletPlanLine, type FileTypeStyle } from "@oura/docs";
import type { SetupPart } from "../hooks/useMySetup";
import { asksOf, isOpen, lateDaysOf, needsYou, type Ask, type TimingPart } from "./asks";

/** Questions wait on them: the doc needs their reply. */
export const docNeedsYou = (doc: MerchantDocument): boolean => doc.state === "needs_your_reply";

/** Past its reply-by with questions still waiting on them. Late exists only where the server says so. */
export const isDocLate = (doc: MerchantDocument): boolean => docNeedsYou(doc) && doc.late === true;

/** The file type's icon, tone and word; an unknown type reads as a plain file. */
export const fileTypeOf = (doc: Pick<MerchantDocument, "fileType">): FileTypeStyle => FILE_TYPE[doc.fileType] ?? FILE_TYPE.other;

/**
 * What the merchant calls a doc. "Answer Plan (S1, S3, B1, G4)" reads "Answer
 * Plan": codes mean nothing to them. "GBP Pack Mont Kiara (S2-MK)" reads
 * "Google profile, Mont Kiara", and "Copy Pack (B1)" reads "Page wording".
 */
export function docTitle(doc: Pick<MerchantDocument, "title" | "custom">): string {
    // One rule with the bell's (merchantDocName, clientDocsRules.ts), so a notice and the list name a doc alike.
    // A custom doc type's title as it is, as the backend's bell and emails say it (ourteam-backend#6520).
    const name = merchantDocName(doc.title, Boolean(doc.custom));
    // Capped at 8 words: a list row and a tab never carry a sentence for a name.
    const words = name.split(/\s+/);
    return words.length > 8 ? `${words.slice(0, 8).join(" ").replace(/[,;:]+$/, "")}…` : name;
}

/** Missing dates sort last. ISO strings, so text order is date order. */
function byDate(a: string | null | undefined, b: string | null | undefined): number {
    if (a === b) return 0;
    if (!a) return 1;
    if (!b) return -1;
    return a < b ? -1 : 1;
}

/** Late first (the oldest was-due first), then the soonest reply-by, then the one shared first. */
export function byDocUrgency(a: MerchantDocument, b: MerchantDocument): number {
    return Number(isDocLate(b)) - Number(isDocLate(a)) || byDate(a.replyBy, b.replyBy) || byDate(a.sharedAt, b.sharedAt);
}

/** The docs that need their reply, most pressing first. [] when docs are not read here. */
export function docsNeedingYou(docs: readonly MerchantDocument[] | undefined): MerchantDocument[] {
    return (docs ?? []).filter(docNeedsYou).sort(byDocUrgency);
}

/** The merchant's groups, in the order they read: what needs them first, then ours, answered, and nothing to answer last. */
export const DOC_GROUP_ORDER: MerchantDocState[] = ["needs_your_reply", "with_us", "approved", "nothing_asked"];

/** Every shared doc by its state. Needs your reply is sorted most pressing first; the rest keep the server's order. */
export function groupDocs(docs: readonly MerchantDocument[]): Record<MerchantDocState, MerchantDocument[]> {
    const out: Record<MerchantDocState, MerchantDocument[]> = { needs_your_reply: [], with_us: [], nothing_asked: [], approved: [] };
    for (const doc of docs) (out[doc.state] ?? out.nothing_asked).push(doc);
    out.needs_your_reply.sort(byDocUrgency);
    return out;
}

/* ------------------------------------------------------------------------- *
 * The queue: every question and document that needs them, in one order.
 * ------------------------------------------------------------------------- */

/** One thing waiting on the merchant: a question we asked, or a document with questions in it. */
export type QueueEntry =
    | { kind: "question"; id: string; title: string; ask: Ask }
    | { kind: "doc"; id: string; title: string; doc: MerchantDocument };

/** A queue entry's key: question and doc ids come from different tables, so the kind is part of it. */
export const queueKey = (e: Pick<QueueEntry, "kind" | "id">): string => `${e.kind === "question" ? "q" : "d"}:${e.id}`;

interface Ranked {
    entry: QueueEntry;
    late: boolean;
    /** Reply-by, date-only. */
    due: string | null | undefined;
    /** When it reached them: the question's send date, the doc's share date. */
    since: string | null | undefined;
}

/**
 * Everything waiting on the merchant, most pressing first: the questions that
 * need them (`needsYou`) and the docs with questions waiting on them. Late
 * first, the oldest was-due first; then the soonest reply-by; then the one
 * that reached them first. "Start answering" walks it in this order, and its
 * length is the count on the nav badge, Home, Decisions and Work.
 */
export function needsYouQueue(payload: Pick<ClientPagePayload, "packs" | "documents">): QueueEntry[] {
    const ranked: Ranked[] = [
        ...asksOf(payload.packs)
            .filter((a) => needsYou(a.item))
            .map((ask): Ranked => ({
                entry: { kind: "question", id: ask.item.id, title: ask.item.title, ask },
                late: lateDaysOf(ask.item) > 0,
                // A reply of ours waiting on them has no reply-by of its own.
                due: isOpen(ask.item) ? ask.item.replyBy : null,
                since: ask.pack.publishedAt,
            })),
        ...docsNeedingYou(payload.documents).map((doc): Ranked => ({
            entry: { kind: "doc", id: doc.id, title: docTitle(doc), doc },
            late: isDocLate(doc),
            due: doc.replyBy,
            since: doc.sharedAt,
        })),
    ];
    ranked.sort((a, b) => Number(b.late) - Number(a.late) || byDate(a.due, b.due) || byDate(a.since, b.since));
    return ranked.map((r) => r.entry);
}

/** Everything waiting on the merchant: the queue's length. The nav badge counts with this. */
export function needsYouCount(payload: Pick<ClientPagePayload, "packs" | "documents">): number {
    return needsYouQueue(payload).length;
}

/** Whether a queue entry is late: a question past its reply-by, a doc the server calls late. */
export function isEntryLate(e: QueueEntry): boolean {
    return e.kind === "question" ? lateDaysOf(e.ask.item) > 0 : isDocLate(e.doc);
}

/* ------------------------------------------------------------------------- *
 * Words
 * ------------------------------------------------------------------------- */

/**
 * A doc's one fact while it needs them: "Reply by Wed 30 Sep", or "Was due
 * Thu 24 Sep" in rose once past. Null for a doc that needs nothing, or with
 * no reply-by date (which is never late).
 */
export function docDue(doc: MerchantDocument): TimingPart | null {
    if (!docNeedsYou(doc)) return null;
    const text = merchantReplyByText(doc);
    return text ? { text, late: doc.late === true } : null;
}

/** How many questions in a doc still wait on them. */
export const docWaitingCount = (doc: Pick<MerchantDocument, "questions" | "openQuestions">): number =>
    Math.max(doc.questions?.waiting ?? 0, doc.openQuestions.length);

/** A question's text as the merchant reads it: without a leading "[S2-MK]" code tag. */
export const questionText = (text: string): string => text.replace(/^\s*\[[A-Z]\d+(?:-[A-Z]{2,4})?\]\s*/, "").trim();

/**
 * "Asked Tue 22 Sep", only when a question was asked on a later day than the
 * doc was shared; null otherwise, since the share date already says it. A
 * question written before sharing reads as asked the day they could see it.
 */
export function askedText(askedAt: string | null | undefined, sharedAt?: string | null): string | null {
    const asked = docDate(askedAt);
    if (!asked) return null;
    const shared = docDate(sharedAt);
    if (shared === asked) return null;
    const a = askedAt ? Date.parse(askedAt) : NaN;
    const s = sharedAt ? Date.parse(sharedAt) : NaN;
    if (!Number.isNaN(a) && !Number.isNaN(s) && a < s) return null;
    return `Asked ${asked}`;
}

/**
 * What the doc is for, in the plan's own words: the name of each piece of
 * work its codes cover ("Competitor watch"). A code the plan does not name
 * adds nothing; no code is ever shown. On a one-outlet doc, the code that
 * carries the outlet (the same rule as its chip) drops the plan's "every
 * outlet": "GBP Pack Mont Kiara" is for "Google Business Profile".
 */
export function docPurpose(doc: Pick<MerchantDocument, "codes" | "outlet">, payload: Pick<ClientPagePayload, "engagement" | "plan">): string[] {
    const names = new Map<string, string>();
    for (const d of payload.engagement?.deliverables ?? []) if (d.label) names.set(d.code, d.label);
    for (const i of payload.plan?.items ?? []) if (i.label && !names.has(i.code)) names.set(i.code, i.label);
    const out: string[] = [];
    for (const chip of codeChipsOf(doc.codes, doc.outlet)) {
        const label = names.get(chip.code);
        const name = label ? outletPlanLine(label, chip.outlet) : label;
        if (name && !out.includes(name)) out.push(name);
    }
    return out;
}

/** Codes by area, bare (no outlet suffix). */
const GOOGLE_PROFILE = ["S2", "S4"];
const WEBSITE_PAGES = ["S1", "B1", "G1"];
const SITE_HEALTH = ["S3", "S5", "S6", "S7", "G2"];

/**
 * Not opened yet: only when the server says so (`unread === true`, absent is
 * not known), there is a file to open, and it was not opened on this visit.
 */
export function isDocFresh(doc: MerchantDocument, readNow?: ReadonlySet<string>): boolean {
    return doc.unread === true && Boolean(doc.url) && !readNow?.has(doc.id);
}

/**
 * What a doc is about, in a word or two and title case (it is a name), from
 * the plan codes it carries:
 * the Google profile, the website's pages, site health, the month's results or work,
 * or reporting. The first that matches wins, so a doc with a Google profile
 * code is that whatever else it covers. Null for a doc with no codes: no
 * category rather than a guessed one.
 */
/** Whether a doc's title or file name is the doc type: the whole name, its words before the codes, or
 * one "_" part of the file name ("…_Monthly Work Snapshot_Month 3_…"), never a word inside a longer one. */
function namesType(doc: Partial<Pick<MerchantDocument, "title" | "fileTitle">>, docType: string): boolean {
    // the file's extension off first: "…_Monthly Work Snapshot.pdf" names the type too (Jack, #4925)
    const bare = (value: string) => value.toLowerCase().trim().replace(/\.(docx?|pdf|xlsx?)$/, "").trim();
    const type = docType.toLowerCase();
    const title = bare(doc.title ?? "").replace(/\s*\([^()]*\)\s*$/, "").trim();
    const file = bare(doc.fileTitle ?? "");
    return title === type || file === type || file.split("_").map((x) => x.trim()).includes(type);
}

export function docCategory(doc: Pick<MerchantDocument, "codes"> & Partial<Pick<MerchantDocument, "title" | "fileTitle">>): string | null {
    const codes = doc.codes.map((c) => c.split("-")[0].toUpperCase());
    if (codes.length === 0) return null;
    const has = (set: string[]) => codes.some((c) => set.includes(c));
    if (has(GOOGLE_PROFILE)) return "Google Profile";
    // Every code a monthly one: the monthly doc its title or file name names (ourteam-backend#6868), with
    // one code or more; else, for more than one, the Monthly Update, as before. Never from the codes
    // alone: an older Monthly Update can carry just a new type's codes and must read as it did (Jack, #4925).
    if (codes.every((c) => c.startsWith("G"))) {
        const monthly = DOC_EXPECTED.find((e) => e.eachMonth && namesType(doc, e.docType));
        if (monthly) return monthly.docType;
        if (codes.length > 1) return "Monthly Update";
    }
    if (has(WEBSITE_PAGES)) return "Website Pages";
    if (has(SITE_HEALTH)) return "Site Health";
    return "Reporting";
}

/**
 * "What it's for": the first 2 names, one to a line (a plan's names carry
 * their own commas, "Technical crawl, broken pages and dead links", so a
 * list joined with commas reads as more items than it is), then how many
 * more. Null for none.
 */
export function purposeList(names: string[]): { shown: string[]; more: number } | null {
    if (names.length === 0) return null;
    return { shown: names.slice(0, 2), more: Math.max(0, names.length - 2) };
}

/* ------------------------------------------------------------------------- *
 * Sentences that count questions and documents together
 * ------------------------------------------------------------------------- */

/** A page sentence in two parts: what it says, and a late part drawn in rose. */
export interface SentenceParts {
    text: string;
    late: string | null;
}

/**
 * What is pending and what is done, for the client (#4026 C-FE-6).
 *
 * TWO SOURCES, and the same two the staff Overview counts (#4006):
 *   open asks  -- the questions and documents waiting on the client, which is
 *                 the number the Decisions badge already showed
 *   set-up     -- the access steps that are THE CLIENT'S to do
 *
 * `waiting` is deliberately left out of both figures. An access step the
 * client has marked done and staff have not confirmed is ours, not theirs, and
 * counting it as pending would tell them to do a thing they have done.
 *
 * `null` onboarding -- not read, not served, or no record -- contributes
 * nothing rather than zero. The asks still count, so a failed onboarding read
 * makes this figure SMALL, never wrong in the other direction: it can never
 * claim the client has nothing to do when they do.
 */
export interface PendingCounts {
    /** Open asks plus the client's own open set-up steps. */
    pending: number;
    /** How many of those are late. Asks only: a set-up step has no due date. */
    late: number;
    /** Answered asks plus set-up steps held, over the whole term. */
    completed: number;
    /**
     * Whether the two totals are the whole story.
     *
     * False when the set-up read has not landed or failed: the asks are known
     * and the set-up steps are not, so both figures are LOWER BOUNDS. They
     * must not be printed as exact -- a client with nothing but set-up steps
     * left would read "0 pending" off a read that simply did not arrive.
     */
    exact: boolean;
}

export function pendingCounts(
    payload: Pick<ClientPagePayload, "packs" | "documents" | "decisions">,
    setup: SetupPart,
): PendingCounts {
    const queue = needsYouQueue(payload);
    const answered = (payload.decisions ?? []).length;
    return {
        pending: queue.length + (setup.known ? setup.mine : 0),
        late: queue.filter(isEntryLate).length,
        completed: answered + (setup.known ? setup.done : 0),
        exact: setup.known,
    };
}

/**
 * "6 pending, 4 late · 12 completed", as the words only.
 *
 * It replaces "Nothing needs you", which was not a description of anything and
 * read as an apology on an account that was simply up to date. Zero pending is
 * "0 pending", said plainly: a number a reader can compare with yesterday's.
 *
 * `late` is returned apart so the caller can put it in rose; it is never
 * folded into the text.
 */
export function pendingText(counts: PendingCounts): SentenceParts {
    return {
        text: counts.exact ? `${counts.pending} pending` : `At least ${counts.pending} pending`,
        late: counts.late > 0 ? `${counts.late} late` : null,
    };
}

/** The tail: " · 12 completed". Its own function so both callers print it alike. */
export function completedText(counts: PendingCounts): string {
    return counts.exact ? `${counts.completed} completed` : `at least ${counts.completed} completed`;
}

